// GuidedTour — a lightweight 4-step first-run walkthrough shown on the dashboard
// after a new member finishes profile onboarding. Deliberately dependency-free
// and centered (rather than anchored to specific elements) so it stays robust
// across the mobile/desktop layouts and uses large, high-contrast tap targets
// for an older, less technical audience.

import { useState } from 'react';
import {
  Activity,
  FileText,
  Bot,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  type LucideIcon,
} from 'lucide-react';

interface TourStep {
  icon: LucideIcon;
  title: string;
  body: string;
}

const STEPS: TourStep[] = [
  {
    icon: Activity,
    title: 'Welcome to Iron Forums',
    body: 'This is your dashboard — your home base. It shows your latest snapshot, your Grow Goals, and recent activity from your Forum at a glance.',
  },
  {
    icon: FileText,
    title: 'Start with a Snapshot',
    body: 'Each month you create a Snapshot — a quick self-assessment across the key areas of your life and business. It only takes a few minutes and helps you see where you are right now.',
  },
  {
    icon: Bot,
    title: 'Meet Ask IF',
    body: 'Ask IF is your personal assistant. Ask it to set Grow Goals, find members to connect with, or talk through a snapshot. It is here to help you grow.',
  },
  {
    icon: SettingsIcon,
    title: 'Settings & Help',
    body: 'You can update your profile, control what others see, and manage your account anytime from Settings. Questions? Help is always a tap away.',
  },
];

interface GuidedTourProps {
  /** Called when the member finishes or skips the tour. */
  onClose: () => void;
}

export default function GuidedTour({ onClose }: GuidedTourProps) {
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Welcome tour">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-navy px-6 py-5 flex items-center justify-between">
          <span className="text-white/70 text-sm font-medium">
            Step {step + 1} of {STEPS.length}
          </span>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white flex items-center gap-1 text-sm font-medium"
          >
            Skip <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/15 rounded-2xl mb-5">
            <Icon className="w-9 h-9 text-gold" />
          </div>
          <h2 className="text-2xl font-bold text-navy mb-3">{current.title}</h2>
          <p className="text-gray-600 text-base leading-relaxed">{current.body}</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pb-5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === step ? 'w-6 bg-navy' : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-3 px-6 pb-6">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className={`flex items-center gap-1 px-4 py-3 rounded-lg text-base font-medium transition ${
              step === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-navy hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          {isLast ? (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-lg text-base font-semibold hover:bg-gold-dark transition"
            >
              <Check className="w-5 h-5" />
              Get Started
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg text-base font-semibold hover:bg-navy-dark transition"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
